import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '@frontegg/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  buildSessionEventsUrl,
  cancelSession,
  createSession,
  fetchSessionEvents,
  respondToSessionTool,
  sendSessionMessage,
} from '../../../services/api';
import { AGENT_SESSIONS } from '../../../constants/queryKeys';
import { SESSION_ACTIVE_STATUSES } from '../constants';
import { ChatMessage, ChatMode, ModelProvider } from '../types';
import { AgentContext } from '../../../types';
import {
  INITIAL_TRANSLATION_STATE,
  SessionStreamEvent as SessionEvent,
  SessionTranslationState,
  appendUserMessage,
  translateSessionEvent,
} from './sessionEventTranslator';

const toApiContext = (context?: AgentContext) => {
  if (!context) return undefined;
  return {
    environment_ids: context.environmentIds,
    incident_ids: context.incidentIds,
    asset_ids: context.assetIds,
    test_ids: context.testIds,
  };
};

const TERMINAL_STATUSES = ['idle', 'cancelled', 'failed'];

const MAX_STREAM_RETRIES = 5;
const STREAM_RETRY_BASE_MS = 1000;

type Props = {
  chatHistory?: {
    threadId: string;
    messages: ChatMessage[];
  };
  context?: AgentContext;
  mode?: ChatMode;
  selectedModel?: ModelProvider;
  enabled?: boolean;
};

const useSessionStream = ({
  chatHistory,
  context,
  mode = 'agent',
  selectedModel,
  enabled = true,
}: Props) => {
  const [messages, setMessages] = useState<ChatMessage[]>(
    chatHistory?.messages || [],
  );
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(
    chatHistory?.threadId,
  );

  const sessionIdRef = useRef<string | undefined>(chatHistory?.threadId);
  const abortRef = useRef<AbortController | null>(null);
  const lastSeqRef = useRef<number>(-1);
  const messagesRef = useRef<ChatMessage[]>(chatHistory?.messages || []);
  const translationRef = useRef<SessionTranslationState>({
    ...INITIAL_TRANSLATION_STATE,
  });
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const commitMessages = useCallback((next: ChatMessage[]) => {
    messagesRef.current = next;
    setMessages(next);
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    const id = sessionIdRef.current;
    if (id) {
      void cancelSession(id).catch(() => undefined);
    }
    setIsLoading(false);
  }, []);

  const handleEvent = useCallback(
    (event: SessionEvent) => {
      const seq = event.seq;
      const hasSeq = typeof seq === 'number' && seq >= 0;
      if (hasSeq) {
        if (seq <= lastSeqRef.current) return;
        lastSeqRef.current = seq;
      }
      setIsInitializing(false);

      const { messages: nextMessages, state: nextState } =
        translateSessionEvent(
          messagesRef.current,
          event,
          translationRef.current,
        );
      translationRef.current = nextState;
      if (nextMessages !== messagesRef.current) {
        commitMessages(nextMessages);
      }

      switch (event.type) {
        case 'chunk':
        case 'tool_call':
          setIsLoading(true);
          break;
        case 'status_change':
          if (event.status && TERMINAL_STATUSES.includes(event.status)) {
            setIsLoading(false);
            // The backend SSE stream blocks forever on a terminal session;
            // close it here so the connection doesn't dangle until the next
            // send (or a manual stop).
            abortRef.current?.abort();
            abortRef.current = null;
          } else if (event.status === 'waiting_for_input') {
            // Keep the stream open: the session is paused for a tool response
            // and will resume emitting on the same connection.
            setIsLoading(false);
          } else if (event.status === 'running' || event.status === 'queued') {
            setIsLoading(true);
          }
          void queryClient.invalidateQueries({ queryKey: [AGENT_SESSIONS] });
          break;
        case 'error':
          setError(event.message || 'Agent error');
          setIsLoading(false);
          abortRef.current?.abort();
          abortRef.current = null;
          void queryClient.invalidateQueries({ queryKey: [AGENT_SESSIONS] });
          break;
        default:
          break;
      }
    },
    [queryClient, commitMessages],
  );

  const openStream = useCallback(
    (id: string, fromSeq: number, attempt: number = 0) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const run = async () => {
        try {
          const response = await fetch(buildSessionEventsUrl(id, fromSeq), {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
              Accept: 'application/x-ndjson',
            },
            signal: controller.signal,
          });
          if (!response.ok || !response.body) {
            const err = new Error(`Stream error: ${response.status}`);
            (err as any).fatal =
              response.status >= 400 && response.status < 500;
            throw err;
          }
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            lines.forEach((line) => {
              if (controller.signal.aborted || sessionIdRef.current !== id) {
                return;
              }
              if (!line.trim()) return;
              try {
                handleEvent(JSON.parse(line) as SessionEvent);
              } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('Failed to parse session event', err);
              }
            });
          }
        } catch (err: any) {
          if (controller.signal.aborted) return;
          setIsInitializing(false);
          if (err?.fatal) {
            setError('Lost connection to the conversation');
            setIsLoading(false);
            return;
          }
          if (sessionIdRef.current === id && attempt < MAX_STREAM_RETRIES) {
            const delay = STREAM_RETRY_BASE_MS * 2 ** attempt;
            setTimeout(() => {
              if (sessionIdRef.current === id && !controller.signal.aborted) {
                openStream(id, lastSeqRef.current, attempt + 1);
              }
            }, delay);
          }
        }
      };
      void run();
    },
    [handleEvent, user?.accessToken],
  );

  const sendMessage = useCallback(
    async (message: string, messageContext?: AgentContext) => {
      if (!enabled) return;
      setError(null);
      setIsLoading(true);
      commitMessages(appendUserMessage(messagesRef.current, message));
      translationRef.current.pendingUserEcho += 1;
      const apiContext = toApiContext(messageContext ?? context);
      const model = selectedModel?.modelName;
      try {
        if (!sessionIdRef.current) {
          const { session_id } = await createSession({
            prompt: message,
            mode,
            model,
            context: apiContext,
            environment_id: messageContext?.environmentIds?.[0],
          });
          sessionIdRef.current = session_id;
          setSessionId(session_id);
          lastSeqRef.current = -1;
          void queryClient.invalidateQueries({ queryKey: [AGENT_SESSIONS] });
          openStream(session_id, -1);
        } else {
          const id = sessionIdRef.current;
          try {
            await sendSessionMessage(id, {
              prompt: message,
              mode,
              model,
              context: apiContext,
            });
          } catch (sendErr: any) {
            if (sendErr?.response?.status === 409) {
              await new Promise((resolve) => setTimeout(resolve, 800));
              await sendSessionMessage(id, {
                prompt: message,
                mode,
                model,
                context: apiContext,
              });
            } else {
              throw sendErr;
            }
          }
          openStream(id, lastSeqRef.current);
          void queryClient.invalidateQueries({ queryKey: [AGENT_SESSIONS] });
        }
      } catch (err: any) {
        translationRef.current.pendingUserEcho = Math.max(
          0,
          translationRef.current.pendingUserEcho - 1,
        );
        const last = messagesRef.current[messagesRef.current.length - 1];
        if (last?.role === 'user') {
          commitMessages(messagesRef.current.slice(0, -1));
        }
        setError(err?.response?.data?.detail || 'Failed to send message');
        setIsLoading(false);
      }
    },
    [
      context,
      enabled,
      mode,
      selectedModel,
      openStream,
      queryClient,
      commitMessages,
    ],
  );

  const approveToolUse = useCallback(
    (
      approved: boolean,
      interruptId?: string,
      newArgs?: Record<string, unknown>,
    ) => {
      const id = sessionIdRef.current;
      if (!id || !interruptId) return;
      void respondToSessionTool(id, {
        kind: 'approval',
        tool_call_id: interruptId,
        approved,
        args_override:
          newArgs && Object.keys(newArgs).length > 0 ? newArgs : undefined,
      }).catch((err: any) =>
        setError(
          err?.response?.data?.detail || 'Failed to submit tool response',
        ),
      );
    },
    [],
  );

  const userInteraction = useCallback((val: Record<string, any>) => {
    const id = sessionIdRef.current;
    const toolCallId = translationRef.current.awaitingOutputToolId;
    if (!id || !toolCallId) return;
    void respondToSessionTool(id, {
      kind: 'output',
      tool_call_id: toolCallId,
      value: val,
    }).catch((err: any) =>
      setError(err?.response?.data?.detail || 'Failed to submit tool response'),
    );
  }, []);

  const loadSession = useCallback(
    async (id: string) => {
      abortRef.current?.abort();
      sessionIdRef.current = id;
      setSessionId(id);
      lastSeqRef.current = -1;
      translationRef.current = { ...INITIAL_TRANSLATION_STATE };
      commitMessages([]);
      setError(null);
      setIsLoading(false);
      setIsInitializing(true);
      try {
        const { status, events } = await fetchSessionEvents(id, -1);
        if (sessionIdRef.current !== id) return;
        events.forEach((event) => handleEvent(event as SessionEvent));
        setIsInitializing(false);
        if (status && SESSION_ACTIVE_STATUSES.includes(status)) {
          openStream(id, lastSeqRef.current);
        }
      } catch (err: any) {
        if (sessionIdRef.current !== id) return;
        setIsInitializing(false);
        setError(err?.response?.data?.detail || 'Failed to load conversation');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleEvent, openStream],
  );

  const continueChat = useCallback(() => {
    const id = sessionIdRef.current;
    if (id) openStream(id, lastSeqRef.current);
  }, [openStream]);

  useEffect(() => {
    if (!enabled || !isLoading || !sessionId) return undefined;
    let cancelled = false;
    const tick = async () => {
      // Polling is a fallback for when the live SSE stream isn't connected.
      // While a stream is open (abortRef set) it already delivers events, so
      // skip the redundant /events/history request.
      if (abortRef.current) return;
      try {
        const { status, events } = await fetchSessionEvents(
          sessionId,
          lastSeqRef.current,
        );
        if (cancelled) return;
        events.forEach((event) => handleEvent(event as SessionEvent));
        if (status && status !== 'running' && status !== 'queued') {
          setIsLoading(false);
        }
      } catch {
        // noop
      }
    };
    const interval = setInterval(tick, 2500);
    const initial = setTimeout(tick, 1200);
    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(initial);
    };
  }, [enabled, isLoading, sessionId, handleEvent]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  useEffect(() => {
    if (enabled && chatHistory?.threadId) {
      void loadSession(chatHistory.threadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatHistory?.threadId]);

  const consolidatedMessages = useMemo(
    () =>
      messages
        .filter((message) => !message.hidden)
        .reduce<ChatMessage[]>((result, message) => {
          const lastMessage = result[result.length - 1];
          if (
            message.role === 'assistant' &&
            lastMessage?.role === 'assistant'
          ) {
            lastMessage.parts.push(...message.parts);
            return result;
          }
          result.push({ ...message, parts: [...message.parts] });
          return result;
        }, []),
    [messages],
  );

  return {
    threadId: sessionId || '',
    sendMessage,
    messages,
    consolidatedMessages,
    stop,
    error,
    isLoading,
    isInitializing,
    continueChat,
    approveToolUse,
    userInteraction,
  };
};

export default useSessionStream;
